import { useState, useRef, useEffect } from "react";
import { Button } from "@/modern/components/ui/button";
import { Edit } from "lucide-react";
import { ResourceGeneralEditor } from "@/react/components/entityViews/general/editors/ResourceGeneralEditor";
import { ClientGeneralEditor } from "@/react/components/entityViews/general/editors/ClientGeneralEditor";
import { Dialog } from "@/modern/components/ui/dialog";
import { SharedModal, SharedModalHeader, SharedModalTitle, SharedModalBody } from "@/modern/components/shared-modal-component";
import { useEntityManagerContext } from "../context/EntityManagerContext";
import { Resource } from "@/bryntum-lib/models/Resource";
import { Client } from "@/bryntum-lib/models/Client";
import { cn } from "@/modern/utils";
import { ThemeSwitcher } from "@/modern/components/theme-switcher";
import { EntityMoreMenu } from "./EntityMoreMenu";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";

export function EntityTitleBar() {
  const { record } = useEntityManagerContext();
  const { editorHandler } = useSwivuBryntumContext();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const description = record.description || "";

  useEffect(() => {
    if (descriptionRef.current && description) {
      // Check if the content is truncated by comparing scroll height with client height
      const element = descriptionRef.current;
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [description]);

  return (
    <div className="w-full border-b px-6 py-4 group">
      <div className="flex">
        <div className="space-y-1 flex-1 min-w-0">
          {/* Name with Edit Button */}
          <div className="flex items-center relative text-lg leading-tight gap-2">
            <span className={cn("inline-block truncate max-w-full", record.archived && "line-through")}>{record.name || "N/A"}</span>
            <Button
              size="xs"
              variant="ghost"
              className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex h-6 w-6 p-0"
              onClick={() => {
                if (record.isTask) {
                  editorHandler.openEditor(EDITOR_KEYS.taskEditor, record, { task: record }, { useExisting: true, by: "recordId", className: "dashboard-theme" });
                } else {
                  setSettingsOpen(true);}
                }}

              title="Edit"
            >
              <Edit className="h-3.5 w-3.5" />
            </Button>
          </div>
          {/* Description with Show More */}
          {description && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <div ref={descriptionRef} className="line-clamp-1 break-words flex-1 min-w-0">
                {description}
              </div>
              {isTruncated && (
                <button className="text-primary hover:underline font-medium focus:outline-none whitespace-nowrap" onClick={() => setDescriptionDialogOpen(true)}>
                  Show more
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ThemeSwitcher />
          <EntityMoreMenu />
        </div>
      </div>

      {record.isResource && <ResourceGeneralEditor record={record as Resource} open={settingsOpen} onOpenChange={setSettingsOpen} />}
      {record.isClient && <ClientGeneralEditor record={record as Client} open={settingsOpen} onOpenChange={setSettingsOpen} />}

      {/* Description Dialog */}
      <Dialog open={descriptionDialogOpen} onOpenChange={setDescriptionDialogOpen}>
        <SharedModal className="max-w-3xl">
          <SharedModalHeader>
            <SharedModalTitle>Full Description</SharedModalTitle>
          </SharedModalHeader>

          <SharedModalBody className="pb-4">
            <div className="text-sm leading-loose whitespace-pre-wrap break-words max-h-[65vh] overflow-y-auto overflow-x-hidden pr-2 text-foreground/90 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/30">
              {description}
            </div>
          </SharedModalBody>
        </SharedModal>
      </Dialog>
    </div>
  );
}
